import { Component, Fragment, type ReactNode } from 'react'
import { toast } from 'sonner'
import { ErrorScreen } from '@/app/ErrorScreen'
import { EDIT_ACTION_LABELS } from '@/core/history'
import { markRecentEditClosed } from '@/core/persistence'
import { captureError, ReportingStage, surfaceError } from '@/core/reporting'
import { useEditSession } from '@/core/store/editSession'

interface ErrorBoundaryProps {
  children: ReactNode
  onRecovered?: () => void
}

interface ErrorBoundaryState {
  error: Error | null
  recoveryKey: number
}

// React error boundaries require a class component; this is the app's only class-component exception.
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, recoveryKey: 0 }
  private recovering = false

  private finishRecovery(): void {
    this.setState(
      (current) => ({ error: null, recoveryKey: current.recoveryKey + 1 }),
      () => { this.recovering = false },
    )
  }

  private retryRecovery = (): void => {
    this.recovering = false
    this.setState((current) => ({ error: null, recoveryKey: current.recoveryKey + 1 }))
  }

  static getDerivedStateFromError(error: unknown): Pick<ErrorBoundaryState, 'error'> {
    return {
      error: error instanceof Error
        ? error
        : new Error('Unknown application error', { cause: error }),
    }
  }

  componentDidCatch(error: Error): void {
    captureError(error)
    if (this.recovering) return
    this.recovering = true
    try {
      const state = useEditSession.getState()
      if (state.history.past.length > 0 && state.document) {
        const action = state.undo()
        toast.error(`Undid "${EDIT_ACTION_LABELS[action]}" — it couldn't be displayed`, {
          action: {
            label: 'Redo',
            onClick: () => {
              try {
                useEditSession.getState().redo()
              } catch (redoError) {
                surfaceError(redoError, 'Could not redo the recovered edit.')
              }
            },
          },
        })
        this.props.onRecovered?.()
        this.finishRecovery()
        return
      }

      const recordId = state.persistedEditId ?? state.recoveryBlockedEditId
      if (recordId) state.setRecoveryBlockedEditId(recordId)
      state.clearSource()
      if (recordId) {
        void markRecentEditClosed(recordId)
          .then(() => useEditSession.getState().setRecoveryBlockedEditId(null))
          .catch((closeError: unknown) => {
            surfaceError(closeError, 'Could not close the recovered recent edit.', {
              stage: ReportingStage.Persistence,
            })
          })
      }
      toast.error("Couldn't open your last edit — it's still in Recents", {
        action: {
          label: 'Open Recents',
          onClick: () => useEditSession.getState().setRecentsOpen(true),
        },
      })
      this.props.onRecovered?.()
      this.finishRecovery()
    } catch (recoveryError) {
      const normalized = captureError(recoveryError, 'Could not recover from the render error.')
      this.setState({ error: normalized })
    }
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <ErrorScreen
          error={this.state.error}
          onTryAgain={this.retryRecovery}
        />
      )
    }
    return <Fragment key={this.state.recoveryKey}>{this.props.children}</Fragment>
  }
}
